/**
 * YoyMonthlyView.jsx
 *
 * Monthly view of YOY Forecast table.
 * Supports inline editing (shared YoyEditPopup), flags display, and median preview.
 */

import React, { useMemo, useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';

import DaysSupplyBreakdownPopup from './DaysSupplyBreakdownPopup';

import {
  getMonthlySalesTotal,
  buildMonthlySalesIndex,
  getMonthlySalesTotalFromIndex,
  calculateMonthlyAverageGrowthFromIndex,
  calculateMonthlyForecastFromIndex,
  calculateMonthlyPercentageChange,
  calculateMonthlySeasonalityForecast,
  getCurrentMonthInfo,
  getHistoricalMonths,
  getForecastMonths,
  calculateDaysSuppliedMonthly,
} from '../utils/yoyMonthlyCalculations';

import { renderDaysSuppliedCell, getDueDateClass, calculateDueDate } from '../utils/yoyCalculations';

// Ключи данных агента ('2026-08')
const aiMonthKey = (y, m) => `${y}-${String(m).padStart(2, '0')}`;

// Совпадение прогноза с фактом, % (как в недельном виде): 100% = точное
// попадание; шкала = меньшее/большее — симметрична и не схлопывается в 0
const forecastMatchPct = (fc, actual) => {
  if (typeof fc !== 'number' || typeof actual !== 'number') return null;
  if (fc === 0 && actual === 0) return 100;
  const hi = Math.max(fc, actual);
  if (hi <= 0) return null;
  return Math.round((Math.min(fc, actual) / hi) * 100);
};
// Отклонение прогноза от факта со знаком (как в недельном виде): −50% =
// прогноз ниже факта (занижен), +100% = вдвое выше (завышен)
const forecastDeviationPct = (fc, actual) => {
  if (typeof fc !== 'number' || typeof actual !== 'number') return null;
  if (fc === actual) return 0;
  if (actual <= 0) return fc > 0 ? Infinity : null;
  return Math.round(((fc - actual) / actual) * 100);
};
const fmtDeviation = (d) =>
  d == null ? null : `${d > 0 ? '+' : ''}${Math.min(d, 999)}%`;
const renderDeviation = (dev) => (dev == null || dev === 0) ? null : (
  <span className={`hist-dev ${dev < 0 ? 'dev-under' : 'dev-over'}`}>{fmtDeviation(dev)}</span>
);
// ── Дилы Amazon (как в недельном виде): кружочек у продаж месяца,
// пересекающегося с дилом; тултип — даты и юниты завершённого дила ──────────
const monthRangeStr = (year, month) => {
  const mm = String(month).padStart(2, '0');
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { start: `${year}-${mm}-01`, end: `${year}-${mm}-${String(last).padStart(2, '0')}` };
};
const dealsForRange = (deals, start, end) =>
  (deals || []).filter(d => d.start_date <= end && d.end_date >= start);
const fmtDealDate = (s) =>
  new Date(`${s}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dealLines = (deals) => deals.map(d => {
  const range = `${fmtDealDate(d.start_date)} – ${fmtDealDate(d.end_date)}`;
  const label = d.type || 'Deal';
  if (d.status === 'completed') {
    return `${label}: ${range}${d.units_sold != null ? ` — ${d.units_sold} units sold during the deal` : ' (completed)'}`;
  }
  return `${label}: scheduled for ${range}`;
});
// Тултип по наведению И попап по клику (onInfo, карточки дилов)
const renderDealDot = (deals, onInfo) => {
  if (!deals || !deals.length) return null;
  const lines = dealLines(deals);
  return (
    <span
      className="deal-dot"
      title={lines.join('\n')}
      onClick={onInfo ? (e) => { e.stopPropagation(); onInfo({ title: 'Amazon Deals', deals }); } : undefined}
    />
  );
};

// Значение агента в ячейке строки AI Forecast: значение + прирост % к
// прошлому году + мелкий % точности + «?» (тултип по наведению И попап по
// клику — hover у менеджера не срабатывает). Как в недельном виде.
const renderAiValue = (rec, prevValue, onInfo) => {
  if (!rec || typeof rec.value !== 'number') return '—';
  const tip = [];
  if (rec.p10 != null) tip.push(`P10 (conservative) — ${rec.p10}`);
  if (rec.p50 != null) tip.push(`P50 (median) — ${rec.p50}`);
  if (rec.p90 != null) tip.push(`P90 (optimistic) — ${rec.p90}`);
  if (rec.accuracy != null) tip.push(`Self-assessed accuracy — ${rec.accuracy}%`);
  let change = null;
  if (typeof prevValue === 'number' && prevValue > 0) {
    const pct = Math.round(((rec.value - prevValue) / prevValue) * 100);
    change = `${pct >= 0 ? '+' : ''}${pct}%`;
  }
  const rows = [];
  if (rec.p10 != null) rows.push({ label: 'P10 (conservative)', value: rec.p10.toLocaleString() });
  if (rec.p50 != null) rows.push({ label: 'P50 (median)', value: rec.p50.toLocaleString() });
  if (rec.p90 != null) rows.push({ label: 'P90 (optimistic)', value: rec.p90.toLocaleString() });
  if (rec.accuracy != null) rows.push({ label: 'Self-assessed accuracy', value: `${rec.accuracy}%` });
  if (change) rows.push({ label: 'Change vs last year', value: change, accent: true });
  return (
    <span className="ai-value-wrap">
      {rec.value.toLocaleString()}
      {change && (
        <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>{change}</span>
      )}
      {/* accuracy в ячейке не показываем (просьба менеджера) — только в тултипе/попапе */}
      {tip.length > 0 && (
        <span
          className="ai-info-dot"
          title={tip.join('\n')}
          onClick={onInfo ? (e) => { e.stopPropagation(); onInfo({ title: 'AI Forecast Details', rows }); } : undefined}
        >?</span>
      )}
    </span>
  );
};

// ─── Helper: build flat-map key for monthly sales data ───────────────────────
function mkKey(identifier, year, month) {
  return `${identifier}-${year}-${String(month).padStart(2, '0')}`;
}

// ─── Helper: resolve flag CSS class from a record ────────────────────────────
function getFlagClass(record) {
  if (!record || !record.has_flag) return '';
  if (record.best_seller)    return 'flag-best-seller';
  if (record.lightning_deal) return 'flag-lightning-deal';
  if (record.new_product)    return 'flag-new-product';
  if (record.out_of_stock)   return 'flag-out-of-stock';
  return '';
}

// ─── Main component ───────────────────────────────────────────────────────────

const YoyMonthlyView = ({
  // Products
  filteredProducts,
  productsData,
  identifierField,
  marketplaceConfig,
  activeMarketplace,

  // Sales data  { [marketplace]: { [key]: { value, units, has_flag, best_seller, ... } } }
  salesDataByMarketplace,

  // Config
  visibleColumns,
  showAllPreviousYears,
  expandedRows,
  setExpandedRows,

  // Month counts
  historicalMonthsCount = 6,
  forecastMonthsCount   = 6,

  // Seasonality
  seasonalityFlags,
  getSeasonalityCoefficient,
  toggleSeasonalityFlag,

  // Stock / Days supplied
  activeDaysSuppliedData,
  planToProduceData,
  monthlyPlanData,
  monthlyColumnsExpanded,
  setMonthlyColumnsExpanded,
  monthlyDisplayMonths,

  // Helpers
  calculateBoxCanBeShipped,
  calculateMonthlyDaysSupplied,
  calculateDaysSupplyWithCumulativePlan,
  calculateDaysSupplyWithTotalPlan,
  getMonthlyPlanTooltip,
  getMarketplaceDaysData,
  getTotalStockDaysData,
  getTotalPlanDaysData,
  openEditPopup,          // from YOYForecast — opens the shared popup
  showStockBalanceRow = false,
  showAgenticRow = false,
  aiForecastData = null,  // { ai: {asin: {'2026-08': v}}, page: {…} } — Amazon only
  dealsData = {},         // { ASIN: [{start_date, end_date, type, status, units_sold}] }
  aiPlanData = {},        // { ASIN: qty } — рекомендованный агентом Plan to Produce
  onShowInfo = null,      // (popup {title, lines}) => void — мини-попап деталей
}) => {
  const [daysSupplyPopup, setDaysSupplyPopup] = useState(null);

  const currentMonthInfo = useMemo(() => getCurrentMonthInfo(), []);

  // Объединённые исторические ячейки (rowSpan=2 накрывает строку ИИ) живут
  // вместе со строкой Agentic — условия обязаны совпадать
  const aiMergedOn = showAgenticRow && activeMarketplace === 'amazon';

  const historicalMonths = useMemo(
    () => getHistoricalMonths(currentMonthInfo.month, currentMonthInfo.year, historicalMonthsCount),
    [currentMonthInfo.month, currentMonthInfo.year, historicalMonthsCount]
  );

  const forecastMonths = useMemo(
    () => getForecastMonths(currentMonthInfo.month, currentMonthInfo.year, forecastMonthsCount),
    [currentMonthInfo.month, currentMonthInfo.year, forecastMonthsCount]
  );

  const activeSalesData = useMemo(
    () => salesDataByMarketplace[activeMarketplace] || {},
    [salesDataByMarketplace, activeMarketplace]
  );

  // Pre-build monthly sales indexes per product for O(1) lookups
  const monthlySalesIndexByIdentifier = useMemo(() => {
    const map = {};
    filteredProducts.forEach(product => {
      const identifier = product[identifierField];
      if (identifier) map[identifier] = buildMonthlySalesIndex(identifier, activeSalesData);
    });
    return map;
  }, [filteredProducts, identifierField, activeSalesData]);

  const getMonthTotal = useCallback(
    (identifier, year, month) => {
      const index = monthlySalesIndexByIdentifier[identifier];
      if (index) return getMonthlySalesTotalFromIndex(index, year, month);
      return getMonthlySalesTotal(identifier, year, month, activeSalesData);
    },
    [monthlySalesIndexByIdentifier, activeSalesData]
  );

  // Get the full record (value + flags) directly from the flat map
  const getMonthRecord = useCallback(
    (identifier, year, month) => activeSalesData[mkKey(identifier, year, month)] || null,
    [activeSalesData]
  );

  const getAvgGrowth = useCallback(
    (identifier) => {
      const index = monthlySalesIndexByIdentifier[identifier];
      if (index) return calculateMonthlyAverageGrowthFromIndex(index, historicalMonths);
      return 0;
    },
    [monthlySalesIndexByIdentifier, historicalMonths]
  );

  const getForecastValue = useCallback(
    (product, targetYear, targetMonth, avgGrowth) => {
      const identifier = product[identifierField];
      const config     = marketplaceConfig;
      const index      = monthlySalesIndexByIdentifier[identifier];

      if (config.hasSeasonality && product.niche_category_id && seasonalityFlags?.[product.asin]) {
        const seasonal = calculateMonthlySeasonalityForecast(
          identifier, targetYear, targetMonth,
          product.niche_category_id,
          index || activeSalesData,
          getSeasonalityCoefficient,
          currentMonthInfo.month,
          currentMonthInfo.year
        );
        if (seasonal !== 'N/A') return seasonal;
      }

      if (index) return calculateMonthlyForecastFromIndex(index, targetYear, targetMonth, avgGrowth);
      return 'N/A';
    },
    [identifierField, marketplaceConfig, seasonalityFlags, activeSalesData,
     monthlySalesIndexByIdentifier, getSeasonalityCoefficient, currentMonthInfo]
  );

  // Cache avgGrowth for all rendered products
  const avgGrowthCache = useMemo(() => {
    const map = {};
    filteredProducts.forEach(product => {
      const identifier = product[identifierField];
      if (identifier) map[identifier] = getAvgGrowth(identifier);
    });
    return map;
  }, [filteredProducts, identifierField, getAvgGrowth]);

  // ── Pre-compute all Days Supply values to avoid calling heavy functions in render ──

  // Days Supplied (marketplace stock only) per product
  const marketplaceDaysSuppliedMap = useMemo(() => {
    if (!calculateMonthlyDaysSupplied) return {};
    const map = {};
    filteredProducts.forEach(product => {
      let ms = 0;
      marketplaceConfig.stockColumns.forEach(col => { ms += Number(product[col.key]) || 0; });
      map[product.id] = calculateMonthlyDaysSupplied(product, ms);
    });
    return map;
  }, [filteredProducts, calculateMonthlyDaysSupplied, marketplaceConfig]);

  // Days Supplied (total: WH + marketplace) per product
  const totalDaysSuppliedMap = useMemo(() => {
    if (!calculateMonthlyDaysSupplied) return {};
    const map = {};
    filteredProducts.forEach(product => {
      const wh = Number(product.whStock) || 0;
      let ms = 0;
      marketplaceConfig.stockColumns.forEach(col => { ms += Number(product[col.key]) || 0; });
      map[product.id] = calculateMonthlyDaysSupplied(product, wh + ms);
    });
    return map;
  }, [filteredProducts, calculateMonthlyDaysSupplied, marketplaceConfig]);

  // Days Supply (Total + Plan) per product
  const totalPlanDaysSuppliedMap = useMemo(() => {
    if (!calculateDaysSupplyWithTotalPlan) return {};
    const map = {};
    filteredProducts.forEach(product => {
      map[product.id] = calculateDaysSupplyWithTotalPlan(product);
    });
    return map;
  }, [filteredProducts, calculateDaysSupplyWithTotalPlan]);

  // Days Supply cumulative per product per plan month ('YYYY-MM') — only when expanded
  const cumulativeDaysMap = useMemo(() => {
    if (!monthlyColumnsExpanded || !calculateDaysSupplyWithCumulativePlan) return {};
    const map = {}; // map[product.id][month.key] = days
    filteredProducts.forEach(product => {
      map[product.id] = {};
      monthlyDisplayMonths.forEach(planMonth => {
        map[product.id][planMonth.key] = calculateDaysSupplyWithCumulativePlan(product, planMonth);
      });
    });
    return map;
  }, [filteredProducts, monthlyColumnsExpanded, monthlyDisplayMonths, calculateDaysSupplyWithCumulativePlan]);

  // Tooltips per product per plan month — only when expanded
  const cumulativeTooltipMap = useMemo(() => {
    if (!monthlyColumnsExpanded || !getMonthlyPlanTooltip) return {};
    const map = {};
    filteredProducts.forEach(product => {
      map[product.id] = {};
      monthlyDisplayMonths.forEach(planMonth => {
        const days = cumulativeDaysMap[product.id]?.[planMonth.key];
        map[product.id][planMonth.key] = getMonthlyPlanTooltip(product, planMonth, days);
      });
    });
    return map;
  }, [filteredProducts, monthlyColumnsExpanded, monthlyDisplayMonths, getMonthlyPlanTooltip, cumulativeDaysMap]);



  // ── openMonthEditPopup ────────────────────────────────────────────────────
  // Wraps the shared openEditPopup, passing mode:'monthly' and month so that
  // YoyEditPopup shows the monthly UI and uses the monthly median endpoint.
  // openEditPopup in YOYForecast.js accepts an optional 6th `options` argument
  // that gets merged into the editPopup state.
  const openMonthEditPopup = useCallback((product, month, year, currentValue, field, record) => {
    if (!openEditPopup) return;
    const supportsFlags          = marketplaceConfig.supportsFlags ?? false;
    const medianEndpoint         = marketplaceConfig.endpoints?.medianMonthSales ?? null;
    const medianIdentifierParam  = marketplaceConfig.endpoints?.medianIdentifierParam ?? 'sku';
    openEditPopup(
      product,
      month,
      year,
      currentValue === 'N/A' ? '' : String(currentValue ?? ''),
      field,
      {
        mode:                 'monthly',
        month,
        bestSeller:           supportsFlags ? !!(record?.best_seller)    : false,
        lightningDeal:        supportsFlags ? !!(record?.lightning_deal) : false,
        newProduct:           supportsFlags ? !!(record?.new_product)    : false,
        outOfStock:           supportsFlags ? !!(record?.out_of_stock)   : false,
        supportsFlags,
        medianEndpoint,
        medianIdentifierParam,
      }
    );
  }, [openEditPopup, marketplaceConfig]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  // Historical cell — shows value + YoY change + edit button + flag colour
  const renderHistoricalCell = (identifier, product, year, month, rowClass) => {
    const record     = getMonthRecord(identifier, year, month);
    const value      = record
      ? (record.value !== undefined ? record.value : record.units)
      : getMonthTotal(identifier, year, month);
    const hasFlag    = !!(record?.has_flag);
    const isMedian   = hasFlag && record?.medianValue !== undefined;
    const flagClass  = getFlagClass(record);
    const flagLabel  = hasFlag
      ? record?.best_seller    ? 'Best Seller'
      : record?.lightning_deal ? 'Deal / Promo'
      : record?.new_product    ? 'New Product'
      : record?.out_of_stock   ? 'Out of Stock'
      : 'Flag'
      : '';
    const cellTitle  = hasFlag
      ? `${flagLabel}${isMedian ? ` · Median value (raw: ${record.units})` : ''}`
      : undefined;
    const prevValue  = getMonthTotal(identifier, year - 1, month);
    const change     = rowClass !== 'previous-year'
      ? calculateMonthlyPercentageChange(value, prevValue)
      : null;
    const displayStr = value === 'N/A' ? 'N/A' : (value?.toLocaleString?.() ?? value);
    const mRange = monthRangeStr(year, month);
    const mDeals = rowClass !== 'previous-year'
      ? dealsForRange(dealsData[identifier], mRange.start, mRange.end)
      : [];

    // При включённом Agentic ячейка текущего года объединена со строкой ИИ
    // (rowSpan=2): сетка факт/%изм + наш прогноз и прогноз ИИ с точностью
    const merged = aiMergedOn && rowClass !== 'previous-year';
    return (
      <td
        rowSpan={merged ? 2 : 1}
        className={[
          'historical-cell',
          rowClass,
          hasFlag ? 'has-flag' : '',
          merged ? 'hist-merged' : '',
          openEditPopup ? 'cell-clickable' : '',
        ].filter(Boolean).join(' ')}
        onClick={openEditPopup ? () => openMonthEditPopup(
          product, month, year,
          record?.units ?? (value === 'N/A' ? '' : value),
          rowClass === 'previous-year' ? 'previousYear' : 'historical',
          record
        ) : undefined}
      >
        {merged ? (() => {
          const k = aiMonthKey(year, month);
          const page = (aiForecastData?.page?.[identifier] || {})[k];
          const ai = (aiForecastData?.ai?.[identifier] || {})[k]?.value;
          const actual = typeof value === 'number' ? value : null;
          const ourAcc = forecastMatchPct(page, actual);
          const aiAcc = forecastMatchPct(ai, actual);
          const ourDev = forecastDeviationPct(page, actual);
          const aiDev = forecastDeviationPct(ai, actual);
          const aiWins = ourAcc != null && aiAcc != null && aiAcc !== ourAcc ? aiAcc > ourAcc : null;
          return (
            <div className="hist-grid">
              <div className={['hist-sales', flagClass].filter(Boolean).join(' ')} title={cellTitle || 'Actual sales'}>
                {renderDealDot(mDeals, onShowInfo)}
                {displayStr}
                {isMedian && (
                  <span title={`Median (replaces raw: ${record.units})`} style={{ fontSize: '9px', color: '#e67e22', fontWeight: 'bold', marginLeft: '2px' }}>~</span>
                )}
              </div>
              <div className="hist-change">
                {change && (
                  <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>{change}</span>
                )}
              </div>
              <div className="hist-sub" title="Our forecast for this month (snapshot made before the month ended)">
                {typeof page === 'number' ? page : '—'}
              </div>
              <div className={`hist-sub hist-acc${aiWins === false ? ' acc-win' : ''}`} title="Our forecast match vs actual sales (100% = exact hit) · signed % = deviation: minus — forecast was LOWER than actual (underplanned), plus — higher (overplanned)">
                {ourAcc != null ? `${ourAcc}%` : '—'}
                {renderDeviation(ourDev)}
              </div>
              <div className="hist-sub hist-ai" title="AI forecast (main value, submitted before the month ended)">
                {typeof ai === 'number' ? ai : '—'}
              </div>
              <div className={`hist-sub hist-ai hist-acc${aiWins === true ? ' acc-win' : ''}`} title="AI forecast match vs actual sales (100% = exact hit) · signed % = deviation: minus — forecast was LOWER than actual (underplanned), plus — higher (overplanned)">
                {aiAcc != null ? `${aiAcc}%` : '—'}
                {renderDeviation(aiDev)}
              </div>
            </div>
          );
        })() : (
          <div className={['value-with-edit', flagClass].filter(Boolean).join(' ')} title={cellTitle}>
            <div>
              <span>
                {renderDealDot(mDeals, onShowInfo)}
                {displayStr}
                {isMedian && (
                  <span
                    title={`Median (replaces raw: ${record.units})`}
                    style={{ fontSize: '10px', color: '#e67e22', fontWeight: 'bold', marginLeft: '2px', cursor: 'help' }}
                  >~</span>
                )}
              </span>
              {change && (
                <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>
                  {change}
                </span>
              )}
            </div>
          </div>
        )}
      </td>
    );
  };

  // Current-month cell — shows actual if exists, else forecast; with edit button
  const renderCurrentMonthCell = (identifier, product, avgGrowth, rowClass) => {
    const { year, month } = currentMonthInfo;
    const record     = getMonthRecord(identifier, year, month);
    const actualRaw  = getMonthTotal(identifier, year, month);
    // Use record.value (median when flagged) for display and calculations
    const actual     = record?.value !== undefined ? record.value : actualRaw;
    const forecast   = getForecastValue(product, year, month, avgGrowth);
    const display    = (actual !== 'N/A' && actual > 0) ? actual : forecast;
    const previous   = getMonthTotal(identifier, year - 1, month);
    const change     = rowClass !== 'previous-year'
      ? calculateMonthlyPercentageChange(display, previous)
      : null;
    const hasFlag    = !!(record?.has_flag);
    const isMedian   = hasFlag && record?.medianValue !== undefined;
    const flagClass  = getFlagClass(record);
    const displayStr = display === 'N/A' ? 'N/A' : (display?.toLocaleString?.() ?? display);

    if (rowClass === 'previous-year') {
      const prevActualRaw = getMonthTotal(identifier, year - 1, month);
      const prevRecord    = getMonthRecord(identifier, year - 1, month);
      const prevActual    = prevRecord?.value !== undefined ? prevRecord.value : prevActualRaw;
      const prevIsMedian  = !!(prevRecord?.has_flag) && prevRecord?.medianValue !== undefined;
      const prevStr       = prevActual === 'N/A' ? 'N/A' : (prevActual?.toLocaleString?.() ?? prevActual);
      const prevFlagLabel = prevRecord?.has_flag
        ? prevRecord?.best_seller    ? 'Best Seller'
        : prevRecord?.lightning_deal ? 'Deal / Promo'
        : prevRecord?.new_product    ? 'New Product'
        : prevRecord?.out_of_stock   ? 'Out of Stock'
        : 'Flag'
        : '';
      const prevCellTitle = prevRecord?.has_flag
        ? (prevFlagLabel + (prevIsMedian ? ' · Median value (raw: ' + prevRecord.units + ')' : ''))
        : undefined;
      return (
        <td
          className={['current-week-cell', 'previous-year', prevRecord?.has_flag ? 'has-flag' : '', openEditPopup ? 'cell-clickable' : ''].filter(Boolean).join(' ')}
          onClick={openEditPopup ? () => openMonthEditPopup(
            product, month, year - 1,
            prevRecord?.units ?? (prevActualRaw === 'N/A' ? '' : prevActualRaw),
            'previousYear',
            prevRecord
          ) : undefined}
        >
          <div className={['value-with-edit', getFlagClass(prevRecord)].filter(Boolean).join(' ')} title={prevCellTitle}>
            <div>
              <span>
                {prevStr}
                {prevIsMedian && (
                  <span
                    title={`Median (replaces raw: ${prevRecord.units})`}
                    style={{ fontSize: '10px', color: '#e67e22', fontWeight: 'bold', marginLeft: '2px', cursor: 'help' }}
                  >~</span>
                )}
              </span>
            </div>
          </div>
        </td>
      );
    }

    const curFlagLabel = hasFlag
      ? record?.best_seller    ? 'Best Seller'
      : record?.lightning_deal ? 'Deal / Promo'
      : record?.new_product    ? 'New Product'
      : record?.out_of_stock   ? 'Out of Stock'
      : 'Flag'
      : '';
    const curCellTitle = hasFlag
      ? (curFlagLabel + (isMedian ? ' · Median value (raw: ' + record.units + ')' : ''))
      : undefined;
    return (
      <td
        className={['current-week-cell', 'month-current', hasFlag ? 'has-flag' : '', openEditPopup ? 'cell-clickable' : ''].filter(Boolean).join(' ')}
        onClick={openEditPopup ? () => openMonthEditPopup(
          product, month, year,
          record?.units ?? (actualRaw !== 'N/A' && actualRaw > 0 ? actualRaw : ''),
          'currentMonth',
          record
        ) : undefined}
      >
        <div className={['value-with-edit', flagClass].filter(Boolean).join(' ')} title={curCellTitle}>
          <div>
            <span>
              {/* точка дила живёт в строке AI, когда та включена */}
              {!aiMergedOn && renderDealDot(dealsForRange(dealsData[identifier], monthRangeStr(year, month).start, monthRangeStr(year, month).end), onShowInfo)}
              {displayStr}
              {isMedian && (
                <span
                  title={`Median (replaces raw: ${record.units})`}
                  style={{ fontSize: '10px', color: '#e67e22', fontWeight: 'bold', marginLeft: '2px', cursor: 'help' }}
                >~</span>
              )}
            </span>
            {change && (
              <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>
                {change}
              </span>
            )}
          </div>
        </div>
      </td>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
    <div className="table-responsive">
      <table className="yoy-table yoy-monthly-table">

        {/* ── THEAD ──────────────────────────────────────────────────────── */}
        <thead>
          <tr>
            <th rowSpan="2" className="checkbox-fixed-column">Seasonality</th>
            <th rowSpan="2" className="yoy-fixed-column product-name-head">Product Name</th>
            <th rowSpan="2">{identifierField.toUpperCase()}</th>
            <th rowSpan="2">Bags per box</th>
            <th rowSpan="2">WH Stock</th>

            {marketplaceConfig.stockColumns.map(col => (
              <th key={col.key} rowSpan="2">{col.label}</th>
            ))}

            <th rowSpan="2">Box can be shipped</th>

            {marketplaceConfig.daysSuppliedKey && (
              <th rowSpan="2">Days Supplied ({marketplaceConfig.daysSuppliedKey})</th>
            )}
            <th rowSpan="2">Days Supplied (Total)</th>
            <th rowSpan="2">Due Date</th>
            <th rowSpan="2">Plan to Produce</th>
            <th rowSpan="2" style={{ whiteSpace: 'nowrap' }}>
              Days Supply (Total + Plan)
              <button
                className="expand-monthly-btn"
                onClick={() => setMonthlyColumnsExpanded?.(v => !v)}
                title={monthlyColumnsExpanded ? 'Collapse monthly breakdown' : 'Expand monthly breakdown'}
              >
                <FontAwesomeIcon icon={monthlyColumnsExpanded ? faMinus : faPlus} />
              </button>
            </th>

            {/* Monthly breakdown columns (expandable) */}
            {monthlyColumnsExpanded && (
              <th
                colSpan={monthlyDisplayMonths.length}
                className="section-header monthly-plan-section-header"
              >
                Monthly Plan
              </th>
            )}

            {visibleColumns.historical && historicalMonths.length > 0 && (
              <th colSpan={historicalMonths.length} className="section-header">
                Historical Data ({historicalMonthsCount} months)
              </th>
            )}

            {visibleColumns.currentWeek && (
              <th rowSpan="2" className="current-week-header section-header">
                Current Month ({currentMonthInfo.label} {currentMonthInfo.year})
                <div style={{ fontSize: '10px', fontWeight: 'normal' }}>
                  {currentMonthInfo.dateRange}
                </div>
              </th>
            )}

            {visibleColumns.prediction && forecastMonths.length > 0 && (
              <th colSpan={forecastMonths.length} className="section-header">
                Prediction ({forecastMonthsCount} months)
              </th>
            )}
          </tr>

          <tr>
            {/* Subheaders for monthly plan columns */}
            {monthlyColumnsExpanded && monthlyDisplayMonths.map(({ key, year, label }) => (
              <th key={key} className="monthly-plan-subheader monthly-plan-combined">
                {label} {year}<br />
                <span style={{ fontSize: '9px', fontWeight: 'normal' }}>Plan / Days</span>
              </th>
            ))}

            {visibleColumns.historical && historicalMonths.map((m, idx) => (
              <th key={idx} className="week-header">
                <div>{m.label} {m.year}</div>
                <div style={{ fontSize: '9px', fontWeight: 'normal' }}>{m.dateRange}</div>
              </th>
            ))}

            {visibleColumns.prediction && forecastMonths.map((m, idx) => (
              <th key={idx} className="prediction-header">
                {m.label} {m.year}
              </th>
            ))}
          </tr>
        </thead>

        {/* ── TBODY ──────────────────────────────────────────────────────── */}
        <tbody>
          {filteredProducts.map(product => {
            const identifier = product[identifierField];
            const isExpanded = expandedRows[product.id];
            const boxShipped = calculateBoxCanBeShipped(product.whStock, product.bagsPerBox);
            const avgGrowth  = avgGrowthCache[identifier] ?? 0;
            const daysData   = activeDaysSuppliedData[identifier] || {};

            const renderFixed = (rowSpan) => (
              <>
                <td rowSpan={rowSpan + (aiMergedOn ? 1 : 0)} className="checkbox-fixed-column">
                  {product.niche_category_id ? (
                    <input
                      type="checkbox"
                      checked={seasonalityFlags?.[product.asin] || false}
                      onChange={(e) => toggleSeasonalityFlag?.(product.asin, e.target.checked)}
                    />
                  ) : (
                    <span title="No niche assigned" style={{ color: '#ccc' }}>—</span>
                  )}
                </td>
                <td rowSpan={rowSpan + (aiMergedOn ? 1 : 0)} className="yoy-fixed-column product-cell">{product.productName}</td>
                <td rowSpan={rowSpan + (aiMergedOn ? 1 : 0)} className="identifier-cell">{identifier}</td>
                <td rowSpan={rowSpan + (aiMergedOn ? 1 : 0)} className="bags-cell">{product.bagsPerBox}</td>
                <td rowSpan={rowSpan + (aiMergedOn ? 1 : 0)} className="wh-stock-cell">{product.whStock}</td>

                {marketplaceConfig.stockColumns.map(col => (
                  <td key={col.key} rowSpan={rowSpan + (aiMergedOn ? 1 : 0)} className="stock-cell">
                    {product[col.key] ?? 0}
                  </td>
                ))}

                <td rowSpan={rowSpan + (aiMergedOn ? 1 : 0)} className="ship-cell">{boxShipped}</td>

                {marketplaceConfig.daysSuppliedKey && (
                  <td
                    rowSpan={rowSpan}
                    className={`days-supplied-market-cell${getMarketplaceDaysData ? ' dsp-clickable' : ''}`}
                    onClick={getMarketplaceDaysData ? () => setDaysSupplyPopup(getMarketplaceDaysData(product)) : undefined}
                  >
                    {renderDaysSuppliedCell(
                      calculateMonthlyDaysSupplied
                        ? (marketplaceDaysSuppliedMap[product.id] || 0)
                        : (daysData[marketplaceConfig.daysSuppliedKey] || 0)
                    )}
                  </td>
                )}
                <td
                  rowSpan={rowSpan}
                  className={`days-supplied-total-cell${getTotalStockDaysData ? ' dsp-clickable' : ''}`}
                  onClick={getTotalStockDaysData ? () => setDaysSupplyPopup(getTotalStockDaysData(product)) : undefined}
                >
                  {renderDaysSuppliedCell(
                    calculateMonthlyDaysSupplied
                      ? (totalDaysSuppliedMap[product.id] || 0)
                      : (daysData.total || 0)
                  )}
                </td>
                <td rowSpan={rowSpan} className="due-date-cell">
                  {(() => {
                    const d = calculateMonthlyDaysSupplied
                      ? (marketplaceConfig.daysSuppliedKey
                          ? (marketplaceDaysSuppliedMap[product.id] || 0)
                          : (totalDaysSuppliedMap[product.id] || 0))
                      : (marketplaceConfig.daysSuppliedKey
                          ? (daysData[marketplaceConfig.daysSuppliedKey] || 0)
                          : (daysData.total || 0));
                    return (
                      <div className={`due-date-value ${getDueDateClass(d)}`}>
                        {calculateDueDate(d)}
                      </div>
                    );
                  })()}
                </td>
                <td rowSpan={rowSpan} className="plan-to-produce-cell">
                  {planToProduceData[product.sku] > 0 ? planToProduceData[product.sku] : '—'}
                </td>
                <td
                  rowSpan={rowSpan}
                  className={`days-supplied-plan-cell${getTotalPlanDaysData ? ' dsp-clickable' : ''}`}
                  onClick={getTotalPlanDaysData ? () => setDaysSupplyPopup(getTotalPlanDaysData(product)) : undefined}
                >
                  {renderDaysSuppliedCell(
                    calculateDaysSupplyWithTotalPlan
                      ? (totalPlanDaysSuppliedMap[product.id] || 0)
                      : (daysData.total || 0)
                  )}
                </td>

                {/* Monthly plan breakdown columns (expandable) */}
                {monthlyColumnsExpanded && monthlyDisplayMonths.map(({ key }) => {
                  const skuPlan = monthlyPlanData[product.sku] || {};
                  const monthQty = skuPlan[key] || 0;
                  const daysWithCumPlan = cumulativeDaysMap[product.id]?.[key] || 0;
                  const tooltip = cumulativeTooltipMap[product.id]?.[key];
                  return (
                    <td
                      key={key}
                      rowSpan={rowSpan + (aiMergedOn ? 1 : 0)}
                      className="monthly-plan-cell monthly-plan-combined-cell"
                      title={tooltip}
                    >
                      <div className="monthly-plan-qty">{monthQty > 0 ? monthQty.toLocaleString() : '—'}</div>
                      <div className="monthly-plan-days">{renderDaysSuppliedCell(daysWithCumPlan || 0)}</div>
                    </td>
                  );
                })}
              </>
            );

            return (
              <React.Fragment key={product.id}>

                {/* ── Previous year row ───────────────────────────────── */}
                {isExpanded && (
                  <tr className="previous-year-row">
                    {renderFixed(2)}

                    {/* Historical — previous year */}
                    {visibleColumns.historical && historicalMonths.map((m, idx) => (
                      <React.Fragment key={idx}>
                        {renderHistoricalCell(identifier, product, m.year - 1, m.month, 'previous-year')}
                      </React.Fragment>
                    ))}

                    {/* Current month — previous year */}
                    {visibleColumns.currentWeek && renderCurrentMonthCell(identifier, product, avgGrowth, 'previous-year')}

                    {/* Forecast months — previous year (read-only, no flags) */}
                    {visibleColumns.prediction && forecastMonths.map((m, idx) => {
                      const value = getMonthTotal(identifier, m.year - 1, m.month);
                      return (
                        <td key={idx} className="prediction-cell previous-year">
                          {value === 'N/A' ? 'N/A' : value?.toLocaleString?.() ?? value}
                        </td>
                      );
                    })}
                  </tr>
                )}

                {/* ── Current year row ────────────────────────────────── */}
                <tr className="current-year-row">
                  {!isExpanded && renderFixed(1)}

                  {/* Historical — current year (actual data) */}
                  {visibleColumns.historical && historicalMonths.map((m, idx) => (
                    <React.Fragment key={idx}>
                      {renderHistoricalCell(identifier, product, m.year, m.month, '')}
                    </React.Fragment>
                  ))}

                  {/* Current month */}
                  {visibleColumns.currentWeek && renderCurrentMonthCell(identifier, product, avgGrowth, '')}

                  {/* Forecast months */}
                  {visibleColumns.prediction && forecastMonths.map((m, idx) => {
                    const forecast = getForecastValue(product, m.year, m.month, avgGrowth);
                    const previous = getMonthTotal(identifier, m.year - 1, m.month);
                    const change   = calculateMonthlyPercentageChange(forecast, previous);
                    const fmRange = monthRangeStr(m.year, m.month);
                    const fmDeals = dealsForRange(dealsData[identifier], fmRange.start, fmRange.end);
                    return (
                      <td key={idx} className="prediction-cell">
                        <div className="current-year-value">
                          {!aiMergedOn && renderDealDot(fmDeals, onShowInfo)}
                          {forecast === 'N/A' ? 'N/A' : forecast?.toLocaleString?.() ?? forecast}
                          {change && (
                            <span className={`change-indicator ${change.startsWith('+') ? 'positive' : 'negative'}`}>
                              {change}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* ── Agentic (AI Forecast) row ────────────────────────── */}
                {showAgenticRow && activeMarketplace === 'amazon' && (() => {
                  const aiKey = (y, m) => `${y}-${String(m).padStart(2, '0')}`;
                  const aiData = aiForecastData?.ai?.[identifier] || {};
                  // Days Supply / Due Date по прогнозу агента — та же математика,
                  // что у колонок страницы, но потребление стока по прогнозу
                  // агента; выводится в те же колонки
                  const hasAi = Object.keys(aiData).length > 0;
                  // значения агента — объекты { value, p10, p50, p90, accuracy }
                  const aiForecastFn = (id, y, m) => {
                    const v = aiData[aiKey(y, m)]?.value;
                    return typeof v === 'number' ? v : 'N/A';
                  };
                  const aiFba = Number(product.fbaStock) || 0;
                  const aiAwd = Number(product.awdStock) || 0;
                  const aiWh = Number(product.whStock) || 0;
                  // план агента (рекомендация); без неё дни считаем по нашему плану
                  const aiPlanRec = aiPlanData[identifier];
                  const aiPlanQty = aiPlanRec != null ? aiPlanRec : (planToProduceData[product.sku] || 0);
                  const aiCalcDays = (stock) => calculateDaysSuppliedMonthly(
                    stock, identifier, currentMonthInfo, aiForecastFn);
                  const aiDaysMp = hasAi ? aiCalcDays(aiFba + aiAwd) : null;
                  const aiDaysTotal = hasAi ? aiCalcDays(aiFba + aiAwd + aiWh) : null;
                  const aiDaysPlan = hasAi ? aiCalcDays(aiFba + aiAwd + aiWh + aiPlanQty) : null;
                  return (
                    <tr className="agentic-row">
                      {/* одинаковые с нашей строкой ячейки (чекбокс, имя, ASIN,
                          стоки, месячные планы) накрыты rowSpan сверху; строка
                          начинается со своих 5 колонок по данным агента */}
                      <td className="agentic-cell" title="Days of FBA+AWD stock by the AI forecast">
                        {aiDaysMp !== null ? renderDaysSuppliedCell(aiDaysMp) : '—'}
                      </td>
                      <td className="agentic-cell" title="Days of total stock (WH+FBA+AWD) by the AI forecast">
                        {aiDaysTotal !== null ? renderDaysSuppliedCell(aiDaysTotal) : '—'}
                      </td>
                      <td className="agentic-cell" title="Due date by the AI forecast">
                        {aiDaysMp !== null ? (
                          <div className={`due-date-value ${getDueDateClass(aiDaysMp)}`}>
                            {calculateDueDate(aiDaysMp)}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="agentic-cell" title="AI recommended Plan to Produce">
                        {aiPlanRec != null ? aiPlanRec.toLocaleString() : '—'}
                      </td>
                      <td className="agentic-cell" title="Days of stock incl. AI recommended plan, by the AI forecast">
                        {aiDaysPlan !== null ? renderDaysSuppliedCell(aiDaysPlan) : '—'}
                      </td>

                      {/* Historical cells отсутствуют: их накрывает rowSpan=2
                          объединённой ячейки основной строки */}

                      {/* Current month: точка дила + значение + прирост +
                          «?» (тултип + попап по клику) */}
                      {visibleColumns.currentWeek && (() => {
                        const r = monthRangeStr(currentMonthInfo.year, currentMonthInfo.month);
                        return (
                          <td className="agentic-cell current-week-cell">
                            {renderDealDot(dealsForRange(dealsData[identifier], r.start, r.end), onShowInfo)}
                            {renderAiValue(
                              aiData[aiKey(currentMonthInfo.year, currentMonthInfo.month)],
                              getMonthTotal(identifier, currentMonthInfo.year - 1, currentMonthInfo.month),
                              onShowInfo
                            )}
                          </td>
                        );
                      })()}

                      {/* Forecast months */}
                      {visibleColumns.prediction && forecastMonths.map((m, idx) => {
                        const r = monthRangeStr(m.year, m.month);
                        return (
                          <td key={idx} className="agentic-cell prediction-cell">
                            {renderDealDot(dealsForRange(dealsData[identifier], r.start, r.end), onShowInfo)}
                            {renderAiValue(
                              aiData[aiKey(m.year, m.month)],
                              getMonthTotal(identifier, m.year - 1, m.month),
                              onShowInfo
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })()}

                {/* ── Est. End-of-Month Stock row ──────────────────────── */}
                {showStockBalanceRow && (() => {
                  // Total initial stock: WH + all marketplace stock columns
                  const wh = Number(product.whStock) || 0;
                  let marketStock = 0;
                  marketplaceConfig.stockColumns.forEach(col => {
                    marketStock += Number(product[col.key]) || 0;
                  });
                  const initialStock = wh + marketStock;

                  // Build months to calculate: current month + all forecast months
                  const monthsToCalc = [
                    { year: currentMonthInfo.year, month: currentMonthInfo.month },
                    ...forecastMonths.map(m => ({ year: m.year, month: m.month })),
                  ];

                  // Calculate running balances per month
                  const monthBalances = {};
                  let prevBalance = initialStock;
                  monthsToCalc.forEach(({ year, month }) => {
                    const key = `${year}-${String(month).padStart(2, '0')}`;
                    const forecastVal = getForecastValue(product, year, month, avgGrowth);
                    const forecastNum = typeof forecastVal === 'number' ? forecastVal : 0;
                    const production = (monthlyPlanData[product.sku] || {})[key] || 0;
                    const startBalance = Math.max(0, prevBalance);
                    const endBalance = startBalance + production - forecastNum;
                    monthBalances[key] = {
                      endBalance,
                      forecastNum,
                      tooltip: `Stock at start: ${startBalance.toLocaleString()}\n+ Plan to produce: ${production.toLocaleString()}\n− Forecast sales: ${forecastNum.toLocaleString()}\n= End balance: ${endBalance.toLocaleString()}${prevBalance < 0 ? '\n(Negative carry ignored)' : ''}`,
                    };
                    prevBalance = endBalance;
                  });

                  // How many forecast months does a given endBalance cover,
                  // starting from the month AFTER fromKey.
                  // Returns { months, lines } for display + tooltip breakdown.
                  const calcMonthsOfStock = (endBalance, fromKey) => {
                    if (endBalance <= 0) return { months: 0, lines: [] };
                    let remaining = endBalance;
                    let months = 0;
                    const lines = [];
                    // Find starting index — skip months up to and including fromKey
                    let startIdx = 0;
                    if (fromKey) {
                      const idx = forecastMonths.findIndex(
                        fm => `${fm.year}-${String(fm.month).padStart(2, '0')}` === fromKey
                      );
                      // currentKey is not in forecastMonths → idx === -1 → start from 0
                      startIdx = idx >= 0 ? idx + 1 : 0;
                    }
                    for (let i = startIdx; i < forecastMonths.length; i++) {
                      const fm = forecastMonths[i];
                      const fmKey = `${fm.year}-${String(fm.month).padStart(2, '0')}`;
                      const fmForecast = monthBalances[fmKey]?.forecastNum || 0;
                      if (fmForecast <= 0) {
                        months++;
                        lines.push(`${fm.label}: forecast 0, carry ${remaining.toLocaleString()}`);
                        continue;
                      }
                      if (remaining >= fmForecast) {
                        remaining -= fmForecast;
                        months++;
                        lines.push(`${fm.label}: −${fmForecast.toLocaleString()} → ${remaining.toLocaleString()} left`);
                      } else {
                        // Partial month
                        const daysInMonth = new Date(fm.year, fm.month, 0).getDate();
                        const partial = (remaining / fmForecast * daysInMonth).toFixed(1);
                        lines.push(`${fm.label}: partial (${remaining.toLocaleString()}/${fmForecast.toLocaleString()}, ~${partial}d)`);
                        months = months + remaining / fmForecast;
                        remaining = 0;
                        break;
                      }
                    }
                    return { months: Math.round(months * 10) / 10, lines };
                  };

                  const renderMonthBalanceCell = (key, extraClass = '', colSpan = 1) => {
                    const entry = monthBalances[key];
                    if (!entry) return <td colSpan={colSpan} className={`stock-balance-cell stock-balance-empty ${extraClass}`}>—</td>;
                    const { endBalance, tooltip } = entry;
                    const { months: monthsOfStock, lines } = calcMonthsOfStock(endBalance, key);
                    const cls = endBalance < 0 ? 'balance-negative'
                      : monthsOfStock < 1  ? 'balance-stock-low'
                      : monthsOfStock < 3  ? 'balance-stock-mid'
                      :                      'balance-stock-high';
                    const coverageLines = lines.length > 0
                      ? '\n\nCoverage breakdown:\n' + lines.join('\n')
                      : '';
                    const fullTooltip = tooltip + coverageLines;
                    return (
                      <td colSpan={colSpan} className={`stock-balance-cell ${extraClass} ${cls}`} title={fullTooltip}>
                        {endBalance.toLocaleString()}
                        <span className="stock-weeks-suffix" style={{ opacity: 0.65, fontSize: '0.85em', marginLeft: '3px' }}>
                          /{monthsOfStock}m
                        </span>
                      </td>
                    );
                  };

                  // Count fixed left columns to span (same structure as renderFixed)
                  const fixedColCount =
                    1 + // checkbox
                    1 + // product name
                    1 + // identifier
                    1 + // bags
                    1 + // wh stock
                    marketplaceConfig.stockColumns.length +
                    1 + // box shipped
                    (marketplaceConfig.daysSuppliedKey ? 1 : 0) +
                    1 + // total days
                    1 + // due date
                    1 + // plan to produce
                    1 + // days + plan
                    (monthlyColumnsExpanded ? monthlyDisplayMonths.length : 0);

                  const currentKey = `${currentMonthInfo.year}-${String(currentMonthInfo.month).padStart(2, '0')}`;

                  return (
                    <tr className="stock-balance-row">
                      {/* первые 2 ячейки sticky — та же защита от
                          горизонтального скролла, что у строки AI */}
                      <td className="checkbox-fixed-column stock-balance-label-cell" />
                      <td className="yoy-fixed-column stock-balance-label-cell">
                        <span className="stock-balance-label">Est. End-of-Month Stock</span>
                      </td>
                      {fixedColCount - 2 > 0 && (
                        <td colSpan={fixedColCount - 2} className="stock-balance-label-cell" />
                      )}

                      {/* Historical — empty cells for alignment */}
                      {visibleColumns.historical && historicalMonths.map((_, idx) => (
                        <td key={idx} className="stock-balance-cell stock-balance-empty" />
                      ))}

                      {/* Current month */}
                      {visibleColumns.currentWeek && renderMonthBalanceCell(currentKey, 'current-week-cell', 1)}

                      {/* Forecast months */}
                      {visibleColumns.prediction && forecastMonths.map((m, idx) => {
                        const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
                        return React.cloneElement(
                          renderMonthBalanceCell(key, 'prediction-cell', 1),
                          { key: idx }
                        );
                      })}
                    </tr>
                  );
                })()}

              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
    {daysSupplyPopup && (
      <DaysSupplyBreakdownPopup
        data={daysSupplyPopup}
        onClose={() => setDaysSupplyPopup(null)}
      />
    )}
    </>
  );
};

export default YoyMonthlyView;